import { Loader2, RefreshCw, ShieldAlert, ShieldX } from "lucide-react";
import { Button } from "@/components/ui/button";

type NoAccessProps =
  | { kind: "no-grant"; tabLabel: string; onRefresh: () => void; refreshing?: boolean }
  | { kind: "check-failed"; onRetry: () => void; retrying?: boolean };

/**
 * Full-body replacement for a tab the caller may not use: a direct URL to an
 * ungranted tab, a grant revoked while the tab was open, or et-auth being
 * unreachable so the check could not run at all. The two cases look different
 * on purpose — one is a permission, the other is an outage.
 */
export const NoAccess = (props: NoAccessProps) => {
  if (props.kind === "check-failed") {
    return (
      <div className="flex h-full flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-700">
          <ShieldAlert className="size-7" />
        </div>
        <h2 className="text-lg font-semibold">Access check failed</h2>
        <p className="mt-1 max-w-md text-sm text-muted-foreground">
          The dashboard could not reach the auth service to verify your access.
          Your permissions have not changed; try again in a moment.
        </p>
        <Button className="mt-5 gap-2" variant="outline" onClick={props.onRetry} disabled={props.retrying}>
          {props.retrying ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          Retry
        </Button>
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col items-center justify-center py-16 text-center">
      <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
        <ShieldX className="size-7" />
      </div>
      <h2 className="text-lg font-semibold">No access to {props.tabLabel}</h2>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">
        Your account does not hold this tab. A Dashboard IAM holder can grant
        it; the change takes effect on your next request.
      </p>
      <Button className="mt-5 gap-2" variant="outline" onClick={props.onRefresh} disabled={props.refreshing}>
        {props.refreshing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
        Re-check my access
      </Button>
    </div>
  );
};
